/**
 * §389: Browser Runtime Error Buffer — SDK public surface.
 *
 * Bounded session-backed buffer for unhandled browser exceptions and rejected
 * promises. Entries are persisted in sessionStorage so they survive page
 * reloads and are available to runtimeDebugSnapshot, test mode, and the
 * production telemetry ingress.
 *
 * No secrets, blob contents, or arbitrary state dumps. Only message, stack,
 * location, route, workspace alias, and capture time.
 */

export interface BrowserRuntimeErrorEntry {
    /** Error message (redacted). */
    readonly message: string
    /** Stack trace (truncated to 4 KB). */
    readonly stack?: string
    /** URL where the error occurred. */
    readonly url: string
    /** Line number, if available. */
    readonly line?: number
    /** Column number, if available. */
    readonly column?: number
    /** Route path at time of capture. */
    readonly route?: string
    /** Workspace alias at time of capture, if available. */
    readonly workspaceAlias?: string
    /** ISO-8601 capture timestamp. */
    readonly capturedAt: string
    /** "error" | "unhandledrejection" | "errorboundary" */
    readonly kind: 'error' | 'unhandledrejection' | 'errorboundary'
}

export const MAX_RUNTIME_BROWSER_ERRORS = 20
export const MAX_STACK_TRACE_LENGTH = 4096

const RUNTIME_ERROR_STORAGE_KEY = 'sb:runtime-errors'
const FORWARDED_RUNTIME_ERROR_STORAGE_KEY = 'sb:runtime-errors-forwarded'
const MAX_FORWARDED_RUNTIME_ERRORS = MAX_RUNTIME_BROWSER_ERRORS * 6

let runtimeErrors: BrowserRuntimeErrorEntry[] = []
let forwardedRuntimeErrors: string[] = []

export interface ForwardBrowserRuntimeErrorsOptions {
    endpointUrl: string
    fetch?: typeof fetch
    dedupeWindowMs?: number
    online?: boolean
}

export interface ForwardBrowserRuntimeErrorsResult {
    attempted: number
    sent: number
    skippedAsDuplicate: number
    skippedAsAlreadyForwarded: number
    skippedAsOffline: boolean
}

function isOptionalFiniteNumber(value: unknown): value is number {
    return typeof value === 'undefined' || Number.isFinite(value)
}

function resolveRuntimeErrorUrl(url: string | undefined): string {
    if (typeof url === 'string' && url.length > 0) {
        return url
    }

    if (typeof window !== 'undefined' && typeof window.location?.href === 'string') {
        return window.location.href
    }

    return '(unknown)'
}

function isBrowserRuntimeErrorEntry(value: unknown): value is BrowserRuntimeErrorEntry {
    return typeof value === 'object'
        && value !== null
        && typeof (value as BrowserRuntimeErrorEntry).message === 'string'
        && isOptionalFiniteNumber((value as BrowserRuntimeErrorEntry).line)
        && isOptionalFiniteNumber((value as BrowserRuntimeErrorEntry).column)
        && typeof (value as BrowserRuntimeErrorEntry).url === 'string'
        && typeof (value as BrowserRuntimeErrorEntry).capturedAt === 'string'
        && ['error', 'unhandledrejection', 'errorboundary'].includes(
            (value as BrowserRuntimeErrorEntry).kind,
        )
}

function readPersistedRuntimeErrors(): BrowserRuntimeErrorEntry[] {
    if (typeof sessionStorage === 'undefined') {
        return runtimeErrors
    }

    try {
        const raw = sessionStorage.getItem(RUNTIME_ERROR_STORAGE_KEY)
        if (!raw) {
            return runtimeErrors
        }

        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) {
            return runtimeErrors
        }

        const normalized = parsed
            .filter(isBrowserRuntimeErrorEntry)
            .map(normalizeRuntimeErrorEntry)
            .slice(-MAX_RUNTIME_BROWSER_ERRORS)
        const normalizedRaw = normalized.length > 0 ? JSON.stringify(normalized) : null
        if (normalizedRaw !== raw) {
            persistRuntimeErrors(normalized)
            return normalized
        }

        runtimeErrors = normalized
        return normalized
    } catch {
        return runtimeErrors
    }
}

function persistRuntimeErrors(entries: BrowserRuntimeErrorEntry[]): void {
    runtimeErrors = entries

    if (typeof sessionStorage === 'undefined') {
        return
    }

    try {
        if (entries.length === 0) {
            sessionStorage.removeItem(RUNTIME_ERROR_STORAGE_KEY)
            return
        }

        sessionStorage.setItem(RUNTIME_ERROR_STORAGE_KEY, JSON.stringify(entries))
    } catch {
        // Best-effort persistence only.
    }
}

function readForwardedRuntimeErrors(): string[] {
    if (typeof sessionStorage === 'undefined') {
        return forwardedRuntimeErrors
    }

    try {
        const raw = sessionStorage.getItem(FORWARDED_RUNTIME_ERROR_STORAGE_KEY)
        if (!raw) {
            return forwardedRuntimeErrors
        }

        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) {
            return forwardedRuntimeErrors
        }

        const normalized = parsed
            .filter((value): value is string => typeof value === 'string' && value.length > 0)
            .slice(-MAX_FORWARDED_RUNTIME_ERRORS)
        const normalizedRaw = normalized.length > 0 ? JSON.stringify(normalized) : null
        if (normalizedRaw !== raw) {
            persistForwardedRuntimeErrors(normalized)
            return normalized
        }

        forwardedRuntimeErrors = normalized
        return normalized
    } catch {
        return forwardedRuntimeErrors
    }
}

function persistForwardedRuntimeErrors(keys: readonly string[]): void {
    const normalized = [...new Set(keys)].slice(-MAX_FORWARDED_RUNTIME_ERRORS)
    forwardedRuntimeErrors = normalized

    if (typeof sessionStorage === 'undefined') {
        return
    }

    try {
        if (normalized.length === 0) {
            sessionStorage.removeItem(FORWARDED_RUNTIME_ERROR_STORAGE_KEY)
            return
        }

        sessionStorage.setItem(FORWARDED_RUNTIME_ERROR_STORAGE_KEY, JSON.stringify(normalized))
    } catch {
        // Best-effort persistence only.
    }
}

function truncateStack(stack: string | undefined): string | undefined {
    if (!stack) {
        return undefined
    }
    if (stack.length <= MAX_STACK_TRACE_LENGTH) {
        return stack
    }
    return stack.slice(0, MAX_STACK_TRACE_LENGTH) + '…'
}

function formatUnhandledRejectionReason(reason: unknown): string {
    if (reason instanceof Error) {
        return reason.message
    }

    if (typeof reason === 'string') {
        return reason
    }

    if (reason == null) {
        return '(empty rejection)'
    }

    try {
        const serialized = JSON.stringify(reason)
        if (typeof serialized === 'string' && serialized.length > 0) {
            return serialized
        }
    } catch {
        // Fall back to a best-effort string representation below.
    }

    try {
        const fallback = String(reason)
        return fallback.length > 0 ? fallback : '(empty rejection)'
    } catch {
        return '(unserializable rejection)'
    }
}

function normalizeRuntimeErrorEntry(entry: BrowserRuntimeErrorEntry): BrowserRuntimeErrorEntry {
    const message = typeof entry.message === 'string' ? entry.message.trim() : '(empty error)'
    const url = resolveRuntimeErrorUrl(typeof entry.url === 'string' ? entry.url : undefined)
    const capturedAt = entry.capturedAt ?? new Date().toISOString()

    return {
        message,
        stack: truncateStack(entry.stack),
        url,
        line: Number.isFinite(entry.line) ? entry.line : undefined,
        column: Number.isFinite(entry.column) ? entry.column : undefined,
        route: typeof entry.route === 'string' && entry.route.trim() ? entry.route.trim() : undefined,
        workspaceAlias: typeof entry.workspaceAlias === 'string' && entry.workspaceAlias.trim()
            ? entry.workspaceAlias.trim()
            : undefined,
        capturedAt,
        kind: entry.kind,
    }
}

/**
 * Record a captured runtime error into the bounded buffer.
 * Returns null if the buffer is full and the entry was dropped.
 */
export function recordBrowserRuntimeError(entry: Omit<BrowserRuntimeErrorEntry, 'capturedAt' | 'message' | 'url'> & {
    message?: string
    url?: string
}): BrowserRuntimeErrorEntry | null {
    const message = (entry.message ?? '(empty error)').trim()
    const url = resolveRuntimeErrorUrl(entry.url)

    const entries = [...readPersistedRuntimeErrors(), {
        message,
        stack: truncateStack(entry.stack),
        url,
        line: entry.line,
        column: entry.column,
        route: entry.route,
        workspaceAlias: entry.workspaceAlias,
        capturedAt: new Date().toISOString(),
        kind: entry.kind,
    }].slice(-MAX_RUNTIME_BROWSER_ERRORS)
    persistRuntimeErrors(entries)
    return entries[entries.length - 1]
}

/**
 * Record a window.onerror event.
 */
export function recordBrowserOnError(
    message: string,
    url: string,
    line: number,
    column: number,
    error: Error | undefined,
): BrowserRuntimeErrorEntry | null {
    return recordBrowserRuntimeError({
        message: error?.message ?? message,
        stack: error?.stack,
        url,
        line,
        column,
        kind: 'error',
    })
}

/**
 * Record an unhandled promise rejection.
 */
export function recordBrowserUnhandledRejection(
    reason: unknown,
): BrowserRuntimeErrorEntry | null {
    return recordBrowserRuntimeError({
        message: formatUnhandledRejectionReason(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
        kind: 'unhandledrejection',
    })
}

/**
 * Record a React ErrorBoundary-captured error.
 */
export function recordBrowserErrorBoundary(
    error: Error,
    errorInfo?: { componentStack?: string | null },
): BrowserRuntimeErrorEntry | null {
    const componentStack = errorInfo?.componentStack?.trim()
    const combinedStack = componentStack
        ? `${error.stack ?? ''}\n\nReact component stack:${componentStack}`.trim()
        : error.stack

    return recordBrowserRuntimeError({
        message: error.message,
        stack: combinedStack || undefined,
        kind: 'errorboundary',
    })
}

/**
 * Read recent runtime errors from the buffer.
 */
export function getRecentBrowserRuntimeErrors(limit = MAX_RUNTIME_BROWSER_ERRORS): BrowserRuntimeErrorEntry[] {
    const entries = readPersistedRuntimeErrors()
    if (!Number.isFinite(limit) || limit >= MAX_RUNTIME_BROWSER_ERRORS) {
        return [...entries]
    }

    const normalizedLimit = Math.max(0, Math.trunc(limit))
    if (normalizedLimit === 0) {
        return []
    }

    return entries.slice(-normalizedLimit)
}

/**
 * Clear the runtime error buffer.
 */
export function clearRecentBrowserRuntimeErrors(): void {
    persistRuntimeErrors([])
    persistForwardedRuntimeErrors([])
}

function createForwardedRuntimeErrorKey(entry: BrowserRuntimeErrorEntry): string {
    return JSON.stringify([
        entry.capturedAt,
        entry.kind,
        entry.message,
        entry.url,
        entry.line ?? null,
        entry.column ?? null,
        entry.route ?? null,
        entry.workspaceAlias ?? null,
        entry.stack ?? null,
    ])
}

function createDuplicateRuntimeErrorKey(entry: BrowserRuntimeErrorEntry): string {
    return JSON.stringify([
        entry.message,
        entry.url,
        entry.line ?? null,
        entry.column ?? null,
        entry.route ?? null,
        entry.workspaceAlias ?? null,
    ])
}

function getCapturedAtMs(entry: BrowserRuntimeErrorEntry): number {
    const parsed = Date.parse(entry.capturedAt)
    return Number.isFinite(parsed) ? parsed : 0
}

function pickRuntimeErrorForwardRepresentative(entries: readonly BrowserRuntimeErrorEntry[]): BrowserRuntimeErrorEntry {
    return [...entries].sort((left, right) => {
        const stackLengthDiff = (right.stack?.length ?? 0) - (left.stack?.length ?? 0)
        if (stackLengthDiff !== 0) {
            return stackLengthDiff
        }

        return getCapturedAtMs(left) - getCapturedAtMs(right)
    })[0]!
}

function buildForwardRuntimeErrorEnvelope(entry: BrowserRuntimeErrorEntry) {
    return {
        message: entry.message,
        stack: entry.stack,
        url: entry.url,
        line: entry.line,
        column: entry.column,
        route: entry.route,
        workspaceAlias: entry.workspaceAlias,
        capturedAt: entry.capturedAt,
        kind: entry.kind,
    }
}

function resolveRuntimeErrorFetch(fetchImpl: typeof fetch | undefined): typeof fetch | null {
    if (fetchImpl) {
        return fetchImpl
    }

    if (typeof globalThis.fetch === 'function') {
        return globalThis.fetch.bind(globalThis)
    }

    return null
}

function isRuntimeErrorForwardingOnline(online: boolean | undefined): boolean {
    if (typeof online === 'boolean') {
        return online
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false
    }

    return true
}

type RuntimeErrorForwardBatch = {
    representative: BrowserRuntimeErrorEntry
    forwardedKeys: string[]
}

function buildRuntimeErrorForwardBatches(
    entries: readonly BrowserRuntimeErrorEntry[],
    forwardedKeys: ReadonlySet<string>,
    dedupeWindowMs: number,
): {
    batches: RuntimeErrorForwardBatch[]
    skippedAsAlreadyForwarded: number
    skippedAsDuplicate: number
} {
    const unsentEntries: BrowserRuntimeErrorEntry[] = []
    let skippedAsAlreadyForwarded = 0

    for (const entry of entries) {
        const forwardedKey = createForwardedRuntimeErrorKey(entry)
        if (forwardedKeys.has(forwardedKey)) {
            skippedAsAlreadyForwarded += 1
            continue
        }

        unsentEntries.push(entry)
    }

    const grouped = new Map<string, BrowserRuntimeErrorEntry[]>()
    for (const entry of unsentEntries) {
        const duplicateKey = createDuplicateRuntimeErrorKey(entry)
        const current = grouped.get(duplicateKey)
        if (current) {
            current.push(entry)
        } else {
            grouped.set(duplicateKey, [entry])
        }
    }

    const batches: RuntimeErrorForwardBatch[] = []
    let skippedAsDuplicate = 0

    for (const groupedEntries of grouped.values()) {
        const sortedEntries = [...groupedEntries].sort((left, right) => getCapturedAtMs(left) - getCapturedAtMs(right))
        let cluster: BrowserRuntimeErrorEntry[] = []

        const flushCluster = () => {
            if (cluster.length === 0) {
                return
            }

            const representative = pickRuntimeErrorForwardRepresentative(cluster)
            batches.push({
                representative,
                forwardedKeys: cluster.map(createForwardedRuntimeErrorKey),
            })
            skippedAsDuplicate += cluster.length - 1
            cluster = []
        }

        for (const entry of sortedEntries) {
            if (cluster.length === 0) {
                cluster = [entry]
                continue
            }

            const clusterStartMs = getCapturedAtMs(cluster[0]!)
            const entryMs = getCapturedAtMs(entry)
            if (entryMs - clusterStartMs <= dedupeWindowMs) {
                cluster.push(entry)
                continue
            }

            flushCluster()
            cluster = [entry]
        }

        flushCluster()
    }

    batches.sort((left, right) => getCapturedAtMs(left.representative) - getCapturedAtMs(right.representative))

    return {
        batches,
        skippedAsAlreadyForwarded,
        skippedAsDuplicate,
    }
}

/**
 * Forward buffered runtime errors to the host telemetry ingress.
 */
export async function forwardBrowserRuntimeErrors(
    options: ForwardBrowserRuntimeErrorsOptions,
): Promise<ForwardBrowserRuntimeErrorsResult> {
    const fetchImpl = resolveRuntimeErrorFetch(options.fetch)
    const dedupeWindowMs = Number.isFinite(options.dedupeWindowMs)
        ? Math.max(0, Math.trunc(options.dedupeWindowMs!))
        : 5_000
    const endpointUrl = options.endpointUrl.trim()

    if (!endpointUrl || !fetchImpl) {
        return {
            attempted: 0,
            sent: 0,
            skippedAsDuplicate: 0,
            skippedAsAlreadyForwarded: 0,
            skippedAsOffline: false,
        }
    }

    if (!isRuntimeErrorForwardingOnline(options.online)) {
        return {
            attempted: 0,
            sent: 0,
            skippedAsDuplicate: 0,
            skippedAsAlreadyForwarded: 0,
            skippedAsOffline: true,
        }
    }

    const forwardedKeys = new Set(readForwardedRuntimeErrors())
    const { batches, skippedAsAlreadyForwarded, skippedAsDuplicate } = buildRuntimeErrorForwardBatches(
        getRecentBrowserRuntimeErrors(),
        forwardedKeys,
        dedupeWindowMs,
    )

    let sent = 0

    for (const batch of batches) {
        try {
            const response = await fetchImpl(endpointUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(buildForwardRuntimeErrorEnvelope(batch.representative)),
                keepalive: true,
            })

            if (!response.ok) {
                continue
            }

            sent += 1
            for (const forwardedKey of batch.forwardedKeys) {
                forwardedKeys.add(forwardedKey)
            }
        } catch {
            // Best-effort only.
        }
    }

    persistForwardedRuntimeErrors([...forwardedKeys])

    return {
        attempted: batches.length,
        sent,
        skippedAsDuplicate,
        skippedAsAlreadyForwarded,
        skippedAsOffline: false,
    }
}

/**
 * Format a single runtime error entry for display.
 */
export function formatRuntimeError(entry: BrowserRuntimeErrorEntry): string {
    const kindLabel = entry.kind === 'error' ? '❌' : entry.kind === 'unhandledrejection' ? '⚠️' : '🔴'
    const location = entry.line != null ? `:${entry.line}` : ''
    const column = entry.column != null ? `:${entry.column}` : ''
    const urlPart = entry.url ? ` @ ${entry.url}${location}${column}` : ''
    const routePart = entry.route ? ` [${entry.route}]` : ''
    const workspacePart = entry.workspaceAlias ? ` [ws:${entry.workspaceAlias}]` : ''
    return `${kindLabel} ${entry.message}${urlPart}${routePart}${workspacePart}`
}

/**
 * Format multiple runtime error entries for display.
 */
export function formatRuntimeErrors(entries: readonly BrowserRuntimeErrorEntry[]): string {
    return entries.map((entry, index) => `${index + 1}. ${formatRuntimeError(entry)}`).join('\n')
}

// ── Telemetry forwarding ─────────────────────────────────────────

const _RUNTIME_ERRORS_ENDPOINT = '/api/runtime-errors'
const _CRYPTOBLOBSTORE_API_SUFFIX = '/api/cryptoblobstore'
const _RUNTIME_ERROR_DEDUP_WINDOW_MS = 5000



type _RuntimeErrorPayload = {
    message: string
    stack?: string
    url: string
    line?: number
    column?: number
    route?: string
    workspaceAlias?: string
    kind: string
}

function _buildPayload(entry: BrowserRuntimeErrorEntry): _RuntimeErrorPayload {
    return {
        message: entry.message,
        stack: entry.stack,
        url: entry.url,
        line: entry.line,
        column: entry.column,
        route: entry.route,
        workspaceAlias: entry.workspaceAlias,
        kind: entry.kind,
    }
}

function _resolveForwardBaseUrl(explicitBaseUrl: string | undefined): string {
    if (typeof explicitBaseUrl === 'string' && explicitBaseUrl.trim().length > 0) {
        return explicitBaseUrl.trim()
    }

    if (typeof window === 'undefined') {
        return ''
    }

    const runtimeBaseUrl = (window as unknown as { __API_BASE__?: string }).__API_BASE__
    return typeof runtimeBaseUrl === 'string' ? runtimeBaseUrl.trim() : ''
}

function _resolveRuntimeErrorsEndpoint(baseUrl: string): string {
    const normalized = baseUrl.replace(/\/+$/, '')
    if (!normalized) {
        return ''
    }

    const rewritePath = (path: string): string => {
        if (path.endsWith(_RUNTIME_ERRORS_ENDPOINT)) {
            return path
        }

        if (path.endsWith(_CRYPTOBLOBSTORE_API_SUFFIX)) {
            return `${path.slice(0, -_CRYPTOBLOBSTORE_API_SUFFIX.length)}${_RUNTIME_ERRORS_ENDPOINT}`
        }

        if (path.endsWith('/api')) {
            return `${path}/runtime-errors`
        }

        return `${path}${_RUNTIME_ERRORS_ENDPOINT}`
    }

    try {
        const url = new URL(normalized)
        url.pathname = rewritePath(url.pathname.replace(/\/+$/, ''))
        return url.toString().replace(/\/+$/, '')
    } catch {
        return rewritePath(normalized).replace(/\/+$/, '')
    }
}

function _buildDeduplicationKey(entry: BrowserRuntimeErrorEntry): string {
    return [
        entry.kind,
        entry.message,
        entry.url,
        entry.line ?? '',
        entry.column ?? '',
        entry.route ?? '',
    ].join('\x00')
}

function _parseCapturedAt(capturedAt: string): number | null {
    const parsed = Date.parse(capturedAt)
    return Number.isFinite(parsed) ? parsed : null
}

function _sendPayload(endpoint: string, payload: _RuntimeErrorPayload, opts: ForwardBrowserRuntimeErrorsBestEffortOptions): Promise<void> {
    return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
    }).then(response => {
        if (!response.ok) return
        response.json().then((json: { accepted?: boolean }) => {
            if (json.accepted) opts.onForwarded?.(1)
        }).catch(() => { /* best-effort */ })
    }).catch(() => { /* best-effort */ })
}

/**
 * Forward buffered browser runtime errors to the host telemetry endpoint.
 *
 * Silently discards on network failure or non-2xx responses. Deduplicates
 * entries with identical message/location within a 5-second capture window.
 */
export interface ForwardBrowserRuntimeErrorsBestEffortOptions {
    baseUrl?: string
    enabled?: boolean
    onForwarded?: (count: number) => void
    onError?: (err: unknown) => void
}

export function forwardBrowserRuntimeErrorsBestEffort(opts: ForwardBrowserRuntimeErrorsBestEffortOptions = {}): void {
    if (opts.enabled === false) return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return

    const endpoint = _resolveRuntimeErrorsEndpoint(_resolveForwardBaseUrl(opts.baseUrl))
    if (!endpoint) return

    const entries = getRecentBrowserRuntimeErrors(20)
    if (!entries.length) return

    const seen = new Map<string, number>()
    const unique = entries.filter(entry => {
        const ts = _parseCapturedAt(entry.capturedAt)
        if (ts === null) {
            return true
        }

        const key = _buildDeduplicationKey(entry)
        const prev = seen.get(key)
        if (prev !== undefined && Math.abs(ts - prev) <= _RUNTIME_ERROR_DEDUP_WINDOW_MS) return false
        seen.set(key, ts)
        return true
    })

    if (!unique.length) return

    Promise.allSettled(unique.map(entry => _sendPayload(endpoint, _buildPayload(entry), opts)))
        .catch(err => { opts.onError?.(err) })
}
